import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Global audio-consent context.
 *
 * Browsers block autoplay-with-sound until the user has interacted with the
 * page. To honour that but still deliver a smooth "TikTok-lite" feed once the
 * visitor is engaged, we listen for the FIRST click / touch / key event on
 * `document` and flip `audioEnabled` to true. All feed videos consult this
 * flag to decide their initial `muted` state — so as soon as anyone clicks
 * anywhere (Landing CTA, sign-in, feed card body), every subsequent video
 * autoplays with sound.
 *
 * The consent is persisted per browser session so navigating between pages
 * doesn't re-mute everything. Individual feed items can still override with
 * their own mute button.
 */
const AudioCtx = createContext({ audioEnabled: false, enableAudio: () => {} });

const STORAGE_KEY = "ns_audio_enabled";

export function AudioProvider({ children }) {
  const [audioEnabled, setAudioEnabled] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const enableAudio = useCallback(() => {
    setAudioEnabled((cur) => {
      if (cur) return cur;
      try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch {}
      return true;
    });
  }, []);

  useEffect(() => {
    if (audioEnabled) return;
    const handler = () => enableAudio();
    // `capture: true` so we catch the event before per-component handlers
    // (crucial for Radix/shadcn buttons that stopPropagation). Only need it
    // once — after that we remove ourselves.
    const opts = { capture: true, once: true, passive: true };
    document.addEventListener("pointerdown", handler, opts);
    document.addEventListener("touchstart", handler, opts);
    document.addEventListener("keydown", handler, opts);
    return () => {
      document.removeEventListener("pointerdown", handler, { capture: true });
      document.removeEventListener("touchstart", handler, { capture: true });
      document.removeEventListener("keydown", handler, { capture: true });
    };
  }, [audioEnabled, enableAudio]);

  return <AudioCtx.Provider value={{ audioEnabled, enableAudio }}>{children}</AudioCtx.Provider>;
}

export const useAudio = () => useContext(AudioCtx);
